import React, { useEffect, useState } from "react";
import { Plus, Trash2, Edit2, Search, RotateCw } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", auth_type: "password", password: "", is_admin: false });

  const load = async () => {
    const { data } = await api.get("/admin/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const submitCreate = async () => {
    try {
      await api.post("/admin/users", form);
      toast.success("User created");
      setOpenCreate(false);
      setForm({ email: "", name: "", auth_type: "password", password: "", is_admin: false });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const submitEdit = async () => {
    try {
      const payload = { name: editing.name, auth_type: editing.auth_type, is_admin: editing.is_admin, is_active: editing.is_active };
      if (editing._newPassword) payload.password = editing._newPassword;
      await api.patch(`/admin/users/${editing.id}`, payload);
      toast.success("User updated");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this user permanently?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("User deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const reset2fa = async (id, email) => {
    if (!confirm(`Reset all 2FA methods for ${email}? They'll need to re-enroll on next login.`)) return;
    try {
      await api.post(`/admin/users/${id}/reset-2fa`);
      toast.success("2FA reset — user must re-enroll");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const filtered = users.filter((u) => u.email.includes(q.toLowerCase()) || (u.name || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono">// users</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Users</h1>
        </div>
        <Button onClick={() => setOpenCreate(true)} className="bg-white text-zinc-950 hover:bg-zinc-200" data-testid="create-user-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New user
        </Button>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users…" className="pl-9 bg-zinc-950 border-zinc-800 max-w-sm" data-testid="user-search" />
      </div>

      <Card className="bg-zinc-900/60 border-zinc-800 overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-2 border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
          <div className="col-span-4">Email</div>
          <div className="col-span-3">Name</div>
          <div className="col-span-2">Auth</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        <div className="divide-y divide-zinc-800">
          {filtered.map((u) => (
            <div key={u.id} className="grid grid-cols-12 px-4 py-3 items-center hover:bg-zinc-900 text-sm" data-testid={`user-row-${u.id}`}>
              <div className="col-span-4 font-mono text-zinc-300 truncate">{u.email}</div>
              <div className="col-span-3 truncate">{u.name || <span className="text-zinc-600">—</span>}</div>
              <div className="col-span-2">
                <span className={`text-[10px] uppercase font-mono px-2 py-0.5 rounded ${u.auth_type === "entra" ? "bg-blue-500/10 text-blue-400" : "bg-zinc-800 text-zinc-300"}`}>{u.auth_type}</span>
                {u.is_admin && <span className="ml-1.5 text-[10px] uppercase font-mono px-2 py-0.5 rounded bg-amber-500/10 text-amber-400">admin</span>}
              </div>
              <div className="col-span-2 text-xs text-zinc-500">
                {u.is_active ? (u.totp_enabled ? "Active · 2FA" : "Active") : <span className="text-red-400">Disabled</span>}
              </div>
              <div className="col-span-1 flex justify-end gap-1">
                <button onClick={() => reset2fa(u.id, u.email)} title="Reset 2FA" className="p-1.5 hover:bg-zinc-800 rounded" data-testid={`reset-2fa-${u.id}`}><RotateCw className="w-3.5 h-3.5 text-amber-400" /></button>
                <button onClick={() => setEditing({ ...u })} className="p-1.5 hover:bg-zinc-800 rounded" data-testid={`edit-user-${u.id}`}><Edit2 className="w-3.5 h-3.5 text-zinc-400" /></button>
                <button onClick={() => remove(u.id)} className="p-1.5 hover:bg-zinc-800 rounded" data-testid={`delete-user-${u.id}`}><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="p-8 text-center text-sm text-zinc-500">No users.</div>}
        </div>
      </Card>

      {/* Create dialog */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="font-display">Create user</DialogTitle>
            <DialogDescription className="text-zinc-400">New account on this identity provider.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Email</Label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-zinc-950 border-zinc-800 mt-1" data-testid="new-user-email" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-950 border-zinc-800 mt-1" data-testid="new-user-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Auth type</Label>
              <Select value={form.auth_type} onValueChange={(v) => setForm({ ...form, auth_type: v })}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1" data-testid="new-user-authtype"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="password">Password (+ optional 2FA)</SelectItem>
                  <SelectItem value="entra">Microsoft Entra</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.auth_type === "password" && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Initial password</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-zinc-950 border-zinc-800 mt-1" data-testid="new-user-password" />
              </div>
            )}
            <div className="flex items-center justify-between pt-1">
              <Label className="text-sm">Administrator</Label>
              <Switch checked={form.is_admin} onCheckedChange={(v) => setForm({ ...form, is_admin: v })} data-testid="new-user-admin-toggle" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCreate(false)} className="border-zinc-700">Cancel</Button>
            <Button onClick={submitCreate} className="bg-white text-zinc-950 hover:bg-zinc-200" data-testid="new-user-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="font-display">Edit user</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="font-mono text-xs text-zinc-500">{editing.email}</div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Name</Label>
                <Input value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="bg-zinc-950 border-zinc-800 mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Auth type</Label>
                <Select value={editing.auth_type} onValueChange={(v) => setEditing({ ...editing, auth_type: v })}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="password">Password</SelectItem>
                    <SelectItem value="entra">Entra</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {editing.auth_type === "password" && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">Reset password (optional)</Label>
                  <Input type="password" value={editing._newPassword || ""} onChange={(e) => setEditing({ ...editing, _newPassword: e.target.value })} className="bg-zinc-950 border-zinc-800 mt-1" />
                </div>
              )}
              <div className="flex items-center justify-between">
                <Label>Administrator</Label>
                <Switch checked={editing.is_admin} onCheckedChange={(v) => setEditing({ ...editing, is_admin: v })} />
              </div>
              <div className="flex items-center justify-between">
                <Label>Active</Label>
                <Switch checked={editing.is_active} onCheckedChange={(v) => setEditing({ ...editing, is_active: v })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="border-zinc-700">Cancel</Button>
            <Button onClick={submitEdit} className="bg-white text-zinc-950 hover:bg-zinc-200" data-testid="edit-user-submit">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
